import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import QuickInvoiceModal from '../components/invoice/QuickInvoiceModal';
import LogisticsModal from '../components/order/LogisticsModal';

// Premium Order Show - Mobile-First Responsive
function OrderShow() {
    const { id } = useParams();
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showQuickInvoice, setShowQuickInvoice] = useState(false);
    const [isLogisticsModalOpen, setIsLogisticsModalOpen] = useState(false);
    const [activeLogistics, setActiveLogistics] = useState<'rto' | 'installation' | 'finance' | 'insurance' | 'exchange' | 'delivery' | null>(null);
    const [logisticsCounts, setLogisticsCounts] = useState({
        rto: 0,
        installation: 0,
        finance: 0,
        insurance: 0,
        exchange: 0,
        delivery: 0
    });

    const userRole = (window as any).CURRENT_USER?.role || localStorage.getItem('user_role');
    const isCustomer = userRole === 'customer';
    const isPortalPath = location.pathname.startsWith('/portal');

    useEffect(() => {
        loadOrder();
        loadLogisticsCounts();
    }, [id]);

    const loadOrder = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/orders/${id}`);
            if (response.success) {
                setOrder(response.data);
            } else {
                setError(response.message || 'Failed to load order');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadLogisticsCounts = async () => {
        try {
            const [rtoRes, installRes, financeRes, insuranceRes, exchangeRes, deliveryRes] = await Promise.all([
                api.get(`/orders/${id}/rtos`),
                api.get(`/orders/${id}/installations`),
                api.get(`/orders/${id}/finances`),
                api.get(`/orders/${id}/insurances`),
                api.get(`/orders/${id}/exchanges`),
                api.get(`/orders/${id}/delivery_logs`)
            ]);

            setLogisticsCounts({
                rto: Array.isArray(rtoRes.data) ? rtoRes.data.length : (rtoRes.data?.data?.length || 0),
                installation: Array.isArray(installRes.data) ? installRes.data.length : (installRes.data?.data?.length || 0),
                finance: Array.isArray(financeRes.data) ? financeRes.data.length : (financeRes.data?.data?.length || 0),
                insurance: Array.isArray(insuranceRes.data) ? insuranceRes.data.length : (insuranceRes.data?.data?.length || 0),
                exchange: Array.isArray(exchangeRes.data) ? exchangeRes.data.length : (exchangeRes.data?.data?.length || 0),
                delivery: Array.isArray(deliveryRes.data) ? deliveryRes.data.length : (deliveryRes.data?.data?.length || 0)
            });
        } catch (err) {
            console.error('Error loading logistics counts:', err);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium">Loading order details...</div>
            </div>
        );
    }

    if (error) return <div className="mt-16 p-8 text-center text-rose-600 font-medium">{error}</div>;
    if (!order) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Order not found</div>;

    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            delivered: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
            shipped: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
            processing: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
            confirmed: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
            cancelled: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
            pending: 'bg-slate-50 text-slate-700 ring-1 ring-slate-200'
        };
        return badges[status] || badges.pending;
    };

    const statusSteps = order.order_type === 'store_sale'
        ? ['pending', 'confirmed', 'delivered']
        : ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

    const currentStatusIndex = statusSteps.indexOf(order.status) === -1 ? 0 : statusSteps.indexOf(order.status);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Harmonized with InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        {!isPortalPath && (
                            <Link to="/orders" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            </Link>
                        )}
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Order <span className="text-indigo-600">#{order.order_number}</span>
                        </h1>
                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border shadow-sm ${getStatusBadge(order.status)}`}>
                            {order.status}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px] font-black uppercase tracking-widest border border-slate-200">
                            {order.order_type === 'store_sale' ? 'Direct Sale' : 'Online'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm ml-10">
                        <span className="text-slate-500 font-bold italic">{order.customer_name}</span>
                        {order.invoice_id && (
                            <div className="flex items-center gap-2">
                                <span className="text-slate-300">/</span>
                                <Link to={isPortalPath ? `/portal/invoices/${order.invoice_id}` : `/invoices/${order.invoice_id}`} className="text-emerald-600 hover:text-emerald-700 font-black uppercase text-[10px] tracking-wider hover:underline">
                                    REGISTRY #{order.invoice_number}
                                </Link>
                                <Link to={`/invoices/${order.invoice_id}/print`} target="_blank" className="text-slate-400 hover:text-slate-700 font-bold uppercase text-[10px] tracking-wider hover:underline ml-2 flex items-center gap-1">
                                    🖨️ Print
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {!isCustomer && !order.invoice_id && (
                        <button
                            onClick={() => setShowQuickInvoice(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-all shadow-sm shadow-emerald-100"
                        >
                            Orchestrate Invoice
                        </button>
                    )}
                    {!isCustomer && (
                        <Link
                            to={`/orders/${order.id}/edit`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold hover:bg-black transition-all shadow-sm shadow-slate-200"
                        >
                            Re-architect
                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </Link>
                    )}
                </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-8 overflow-x-auto">
                <div className="min-w-[800px] relative">
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 z-0"></div>
                    <div
                        className="absolute top-1/2 left-0 h-1 bg-indigo-600 -translate-y-1/2 z-0 transition-all duration-1000 ease-out"
                        style={{ width: `${(currentStatusIndex / (statusSteps.length - 1)) * 100}%` }}
                    ></div>

                    <div className="relative z-10 flex justify-between">
                        {statusSteps.map((step, idx) => {
                            const isCompleted = idx <= currentStatusIndex;
                            const isCurrent = idx === currentStatusIndex;

                            return (
                                <div key={step} className="flex flex-col items-center gap-4 group">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500 ${isCurrent ? 'bg-indigo-600 border-indigo-200 text-white scale-125' :
                                        isCompleted ? 'bg-indigo-600 border-indigo-100 text-white' : 'bg-white border-slate-100 text-slate-300'
                                        }`}>
                                        {isCompleted && !isCurrent ? (
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                        ) : (
                                            <span className="text-xs font-black">{idx + 1}</span>
                                        )}
                                    </div>
                                    <span className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${isCompleted ? 'text-indigo-600' : 'text-slate-300'}`}>
                                        {step}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Logic Stream */}
                <div className="lg:col-span-2 space-y-10">
                    {/* Ordered Products – Card Grid */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                            <h2 className="text-base font-bold text-slate-800 flex items-center gap-3">
                                <span className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                                </span>
                                Ordered Products
                            </h2>
                            <span className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600">
                                {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                            </span>
                        </div>

                        {/* Product Cards */}
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {order.items?.map((item: any, idx: number) => (
                                <div
                                    key={item.id || idx}
                                    className="group relative bg-slate-50 hover:bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-md rounded-xl p-4 transition-all duration-200 flex flex-col gap-3"
                                >
                                    {/* Top: icon + product name + serial */}
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-lg flex-shrink-0 group-hover:border-indigo-200 group-hover:bg-indigo-50 transition-colors shadow-sm">
                                            📦
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-slate-800 text-sm truncate group-hover:text-indigo-700 transition-colors flex items-center gap-2">
                                                {item.product_name}
                                                {item.requires_installation && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[9px] font-black uppercase tracking-tighter shadow-sm animate-pulse">
                                                        🔧 Install
                                                    </span>
                                                )}
                                            </h3>
                                            {item.product_unit_identifier && (
                                                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded text-[10px] font-mono font-bold">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" /></svg>
                                                    SN: {item.product_unit_identifier}
                                                </span>
                                            )}
                                        </div>
                                        {/* Quantity badge */}
                                        <div className="flex-shrink-0 text-right">
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-800 text-white rounded-lg text-xs font-black font-mono">
                                                ×{item.quantity}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="h-px bg-slate-200 group-hover:bg-indigo-100 transition-colors" />

                                    {/* Bottom: price breakdown */}
                                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-medium">Unit Price</span>
                                                <span className="font-bold text-slate-700 font-mono">
                                                    ₹{item.unit_price?.toLocaleString()}
                                                </span>
                                            </div>
                                            {item.mrp && (
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-medium text-slate-400">MRP</span>
                                                    <span className="font-bold text-emerald-600/60 font-mono line-through decoration-emerald-200">
                                                        ₹{item.mrp?.toLocaleString()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <span className="text-[10px] text-slate-400 uppercase tracking-wide block">Subtotal</span>
                                            <span className="font-black text-slate-900 font-mono text-base">
                                                ₹{item.total?.toLocaleString()}
                                            </span>
                                        </div>

                                        {/* Discount badge if applicable */}
                                        {item.discount_percent > 0 && (
                                            <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full">
                                                -{item.discount_percent}%
                                            </span>
                                        )}
                                    </div>
                            ))}
                        </div>

                        {/* Summary row */}
                        {order.items?.length > 0 && (
                            <div className="mx-4 mb-4 px-4 py-3 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-between">
                                <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">
                                    {order.items.reduce((sum: number, i: any) => sum + (i.quantity || 0), 0)} total units
                                </span>
                                <span className="text-sm font-black text-indigo-900 font-mono">
                                    ₹{order.subtotal?.toLocaleString()}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Logistics Services Hub */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-8">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                            <h2 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-4">
                                <span className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-lg">🚀</span>
                                Logistics Ecosystem
                            </h2>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.25em] mt-2 opacity-60 italic">Managed Operational Services</p>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[
                                    { label: 'RTO Registration', icon: '🚗', color: 'blue', path: 'rto', desc: 'Regulatory compliance', count: logisticsCounts.rto },
                                    { label: 'Installation', icon: '🔧', color: 'emerald', path: 'installation', desc: 'Deployment services', count: logisticsCounts.installation },
                                    { label: 'Financing', icon: '💎', color: 'amber', path: 'finance', desc: 'Invoiced capital', count: logisticsCounts.finance },
                                    { label: 'Coverage', icon: '🛡️', color: 'purple', path: 'insurance', desc: 'Asset protection', count: logisticsCounts.insurance },
                                    { label: 'Exchange Hub', icon: '🔄', color: 'orange', path: 'exchange', desc: 'Replacement logic', count: logisticsCounts.exchange },
                                    { label: 'Delivery History', icon: '🚚', color: 'indigo', path: 'delivery', desc: 'Tracking & Dispatch', count: logisticsCounts.delivery }
                                ].filter(s => {
                                    if (s.path === 'rto') {
                                        return (window as any).CURRENT_FIRM?.industry === 'vehicle' && (window as any).CURRENT_FIRM?.role_slug !== 'staff';
                                    }
                                    return true;
                                }).map((service) => (
                                    <button
                                        key={service.path}
                                        onClick={() => {
                                            setActiveLogistics(service.path as any);
                                            setIsLogisticsModalOpen(true);
                                        }}
                                        className="group p-6 bg-white border border-slate-200 rounded-xl hover:shadow-md transition-all overflow-hidden text-left"
                                    >
                                        

                                        <div className="relative z-10">
                                            <div className="flex items-center justify-between mb-6">
                                                <div className={`w-14 h-14 bg-white border border-slate-100 rounded-2xl flex items-center justify-center text-2xl shadow-xl shadow-slate-200/50 group-hover:rotate-12 group-hover:scale-110 transition-all duration-500`}>
                                                    {service.icon}
                                                </div>
                                                {service.count > 0 && (
                                                    <span className={`px-3 py-1 bg-${service.color}-600 text-white text-[10px] font-black rounded-lg uppercase tracking-widest shadow-lg shadow-${service.color}-200 animate-in zoom-in duration-500`}>
                                                        {service.count} Linked
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-black text-slate-900 text-sm uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{service.label}</h3>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1 opacity-60">{service.desc}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Registry Metadata */}
                    {(order.shipping_address || order.notes) && (
                        <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl shadow-slate-200/50 p-6 group">
                            <h2 className="text-xl font-black text-slate-900 tracking-tight mb-8 flex items-center gap-4">
                                <span className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center text-xl shadow-inner group-hover:rotate-12 transition-transform">📋</span>
                                Operational Notes
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {order.shipping_address && (
                                    <div className="p-6 bg-slate-50 rounded-xl border border-slate-200 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-4 opacity-5 text-4xl">📍</div>
                                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Destination Protocol</h3>
                                        <p className="text-sm text-slate-700 font-black whitespace-pre-wrap leading-relaxed tracking-tight uppercase">{order.shipping_address}</p>
                                    </div>
                                )}
                                {order.notes && (
                                    <div className="p-6 bg-indigo-50/30 rounded-xl border border-indigo-100 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-4 opacity-5 text-4xl">✍️</div>
                                        <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-4">Internal Log</h3>
                                        <p className="text-sm text-indigo-900/60 font-bold italic whitespace-pre-wrap leading-relaxed">{order.notes}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar Orchestration */}
                <div className="space-y-10">
                    {/* Financial Ledger */}
                    <div className="bg-slate-900 rounded-3xl shadow-2xl shadow-indigo-200 p-6 text-white relative overflow-hidden group">
                        
                        <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.25em] mb-10 flex items-center gap-3 relative z-10">
                            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-ping"></div>
                            Financial Ledger
                        </h2>

                        <div className="space-y-5 relative z-10">
                            <div className="flex justify-between items-center group/item text-[10px] font-black uppercase tracking-[0.2em] opacity-40 hover:opacity-100 transition-opacity">
                                <span>Gross Manifest</span>
                                <span>₹{order.subtotal?.toLocaleString()}</span>
                            </div>
                            {(() => {
                                // @ts-ignore
                                const firmState = (window as any).CURRENT_FIRM?.state || '';
                                const clientState = order.customer?.state || '';
                                const isInterState = firmState && clientState && firmState.toLowerCase() !== clientState.toLowerCase();
                                const taxAmount = order.tax_amount || 0;

                                if (isInterState) {
                                    return (
                                        <div className="flex justify-between items-center group/item text-[10px] font-black uppercase tracking-[0.2em] opacity-40 hover:opacity-100 transition-opacity">
                                            <span>IGST</span>
                                            <span>₹{taxAmount.toLocaleString()}</span>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <>
                                            <div className="flex justify-between items-center group/item text-[10px] font-black uppercase tracking-[0.2em] opacity-40 hover:opacity-100 transition-opacity">
                                                <span>CGST (9%)</span>
                                                <span>₹{(taxAmount / 2).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between items-center group/item text-[10px] font-black uppercase tracking-[0.2em] opacity-40 hover:opacity-100 transition-opacity">
                                                <span>SGST (9%)</span>
                                                <span>₹{(taxAmount / 2).toLocaleString()}</span>
                                            </div>
                                        </>
                                    );
                                }
                            })()}

                            {order.discount_amount > 0 && (
                                <div className="flex justify-between items-center group/item text-[10px] font-black uppercase tracking-[0.2em] text-rose-500">
                                    <span>Rebate Injection</span>
                                    <span>-₹{order.discount_amount?.toLocaleString()}</span>
                                </div>
                            )}
                        </div>

                        <div className="pt-10 mt-10 border-t border-white/10 relative z-10">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] text-right mb-4">Final Valuation</p>
                            <div className="text-5xl font-black tracking-tighter text-right group-hover:scale-105 transition-transform duration-500">
                                ₹{order.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 1 })}
                            </div>
                        </div>
                    </div>

                    {/* Order Identity Registry */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl shadow-slate-200/50 p-6 group">
                        <h2 className="text-xl font-black text-slate-900 tracking-tight mb-8">Registry Metadata</h2>
                        <div className="space-y-8">
                            {[
                                { label: 'Genesis Date', val: new Date(order.order_date).toLocaleDateString(undefined, { dateStyle: 'long' }), icon: '🗓️' },
                                { label: 'Delivery Vector', val: order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString(undefined, { dateStyle: 'long' }) : 'ASAP', icon: '🎯' },
                                { label: 'Architect', val: order.created_by_name || 'System Auto', icon: '👤' }
                            ].map((meta, i) => (
                                <div key={i} className="flex gap-4 group/meta">
                                    <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-lg transition-all duration-300">
                                        {meta.icon}
                                    </div>
                                    <div>
                                        <dt className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">{meta.label}</dt>
                                        <dd className="text-sm font-black text-slate-900 group-hover/meta:translate-x-1 transition-transform">{meta.val}</dd>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Invoice Integration */}
            <QuickInvoiceModal
                isOpen={showQuickInvoice}
                onClose={() => setShowQuickInvoice(false)}
                order={order ? {
                    id: order.id,
                    order_number: order.order_number,
                    customer_id: order.customer_id,
                    customer_name: order.customer_name,
                    status: order.status,
                    items: order.items?.map((item: any) => ({
                        product_id: item.product_id,
                        product_name: item.product_name,
                        quantity: item.quantity,
                        unit_price: item.unit_price,
                        tax_rate: item.tax_rate || 0,
                        discount_percent: item.discount_percent || 0
                    })) || [],
                    subtotal: order.subtotal || 0,
                    tax_amount: order.tax_amount || 0,
                    discount_amount: order.discount_amount || 0,
                    total: order.total || 0
                } : null}
            />

            {/* Logistics Modal */}
            {order && (
                <LogisticsModal
                    isOpen={isLogisticsModalOpen}
                    onClose={() => {
                        setIsLogisticsModalOpen(false);
                        setActiveLogistics(null);
                    }}
                    onSuccess={() => {
                        loadLogisticsCounts(); // Refresh counts on success
                    }}
                    order={order}
                    initialTab={activeLogistics}
                />
            )}
        </div>
    );
}

export default OrderShow;
